import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string-for-all';
import { useLocation } from 'react-router-dom';

import {
  Info,
  Text,
  TextTypes,
  Title,
  ActionType,
} from '@prisjakt/ui-review-form';
import T from '@prisjakt/ui-translate';
import MultiRadio, { Variants } from '@prisjakt/ui-review-form/src/multi-radio';
import { AppContext } from '@prisjakt/ui-app-context/src/app-context';
import StoreInfoCard from '../reviews/components/store-info-card';
import { graphql } from '../../globals/api/requests';

import purchaseReviewPageStoreQuery from './query.graphql';

import { PurchaseReviewPageStoreQuery } from '../../types';
import {
  Errors,
  GraphQLError,
  Questions,
  ShopReviewFormData,
} from '../reviews/types';

import PageContent from '../../components/page-content';
import { useIsAuthenticated } from '../../hooks/use-is-authenticated';

import {
  ShopReviewType,
  storeRated,
  storeReviewCreated,
} from '../reviews/tracking';

import * as S from './styles';
import { AppState } from '../../reducers';
import { useReviewErrorToast } from '../reviews/hooks/use-review-error-toast';
import { saveReviewDraft } from '../reviews/actions';

type Props = {
  data: PurchaseReviewPageStoreQuery;
};

const CreateVerifiedShopReviewPage = ({ data }: Props) => {
  const { store } = data;
  const { t, market } = useContext(AppContext);
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const dispatchReviewErrorToast = useReviewErrorToast();
  const dispatch = useDispatch();
  const drafts = useSelector((state: AppState) => state.areas.review.drafts);
  const createReviewDraft = drafts?.instore?.create[store?.id ?? ''];
  const me = useSelector((state: AppState) => state.areas.account.me);
  const [showThankYouScreen, setShowThankYouScreen] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);

  const handleSubmit = async (formData: ShopReviewFormData) => {
    setPending(true);

    // TODO: Add proper error message
    if (!store?.id && !store?.name) {
      dispatchReviewErrorToast(Errors.UNKNOWN_ERROR);
      return;
    }

    const { code } = queryString.parse(location.search);

    let email = formData[Questions.Email];

    if (isAuthenticated) {
      email = me.email;
    }

    const rating = Number(formData[Questions.Rating]) * 2;

    const response = await graphql(
      `
        mutation CreateVerifiedShopReview(
          $shopId: Int!
          $code: String!
          $rating: Int!
          $reviewText: String
          $answers: [AnswerInput]
          $email: String!
        ) {
          createVerifiedShopReview(
            shopId: $shopId
            rating: $rating
            reviewText: $reviewText
            answers: $answers
            email: $email
            code: $code
          ) {
            id
          }
        }
      `,
      {
        shopId: store.id,
        rating,
        answers: [
          { id: Questions.Recommend, answer: formData[Questions.Recommend] },
        ],
        email,
        code,
      }
    );

    if (response.errors?.length > 0) {
      response.errors.forEach((error: GraphQLError) => {
        dispatchReviewErrorToast(error.code);
      });
      setPending(false);
    } else {
      dispatch(
        saveReviewDraft({ instore: { create: { [store?.id ?? '']: {} } } })
      );
      setShowThankYouScreen(true);
      storeReviewCreated({
        type: ShopReviewType.PURCHASE,
        storeId: store?.id,
        storeName: store?.name,
      });
      storeRated({
        type: ShopReviewType.PURCHASE,
        rating,
        storeId: store?.id,
        storeName: store?.name,
      });
    }
  };

  const handleOnChange = (formData: ShopReviewFormData) => {
    dispatch(
      saveReviewDraft({ instore: { create: { [store?.id ?? '']: formData } } })
    );
  };

  return (
    <PageContent>
      {!showThankYouScreen ? (
        <S.PurchaseReviewForm
          onSubmit={handleSubmit}
          actionType={ActionType.CREATE}
          pending={pending}
          onChange={handleOnChange}
        >
          <S.Header>{t('reviews', 'store.form-title')}</S.Header>
          <StoreInfoCard
            storeId={data?.store?.id}
            storeName={data?.store?.name || ''}
            featured={data?.store?.featured}
            hasLogo={data?.store?.hasLogo}
            reviewCount={data?.store?.userReviewSummary.count}
          />
          {market.shopOfTheYear?.year && (
            <T domain="reviews">store.soty-info.md</T>
          )}
          <div>
            <Title>{t('reviews', 'store.question-rating')}</Title>
            <MultiRadio
              name={Questions.Rating}
              variant={Variants.rate}
              options={[1, 2, 3, 4, 5]}
              optionValues={[
                t('reviews', 'rating.terrible'),
                t('reviews', 'rating.bad'),
                t('reviews', 'rating.ok'),
                t('reviews', 'rating.good'),
                t('reviews', 'rating.excellent'),
              ]}
              required
              currentValue={
                createReviewDraft
                  ? Number(createReviewDraft[Questions.Rating])
                  : undefined
              }
            />
          </div>
          <div>
            <Title>
              {t('reviews', 'store.question-recommendation', {
                name: data?.store?.name || '',
              })}
            </Title>

            <MultiRadio
              name={Questions.Recommend}
              variant={Variants.scale}
              options={[1, 2, 3, 4, 5]}
              optionValues={[
                t('reviews', 'store.answer-recommendation-1'),
                t('reviews', 'store.answer-recommendation-2'),
                t('reviews', 'store.answer-recommendation-3'),
                t('reviews', 'store.answer-recommendation-4'),
                t('reviews', 'store.answer-recommendation-5'),
              ]}
              labels={[
                t('reviews', 'store.answer-recommendation-1'),
                '',
                t('reviews', 'store.answer-recommendation-3'),
                '',
                t('reviews', 'store.answer-recommendation-5'),
              ]}
              required
              currentValue={
                createReviewDraft
                  ? Number(createReviewDraft[Questions.Recommend])
                  : undefined
              }
            />
          </div>
          {!isAuthenticated && (
            <div>
              <Title>{t('reviews', 'dialog.email-label')}</Title>
              <Text
                name={Questions.Email}
                type={TextTypes.Email}
                placeholder={t('account', 'input.email')}
                required
              />

              <Info>{t('reviews', 'form.email-usage-policy')}</Info>

              <Title>
                {t('reviews', 'form.user-login-promo')}{' '}
                <S.Link to="/login">
                  {t('account', 'sign-in-promo.sign-in-button')}
                </S.Link>{' '}
                {t('reviews', 'form.user-login-promo-connect')}
              </Title>
            </div>
          )}
        </S.PurchaseReviewForm>
      ) : (
        <S.ShopReviewThankYou year={2020} />
      )}
    </PageContent>
  );
};

type QueryVariables = {
  // eslint-disable-next-line camelcase
  shop_id: string;
  code: string;
};

CreateVerifiedShopReviewPage.variables = (props: QueryVariables) => {
  return { id: props.shop_id, code: props.code };
};

CreateVerifiedShopReviewPage.graphQL = purchaseReviewPageStoreQuery;

export default CreateVerifiedShopReviewPage;
